/** Persist recently opened CAD files (metadata + optional FileSystemFileHandle). */

const DB_NAME = '3d-viewer';
/** Database name before the app was renamed; its records are moved over once. */
const LEGACY_DB_NAME = 'web-step-viewer';
const DB_VERSION = 1;
const STORE = 'recent';
const MAX_RECENT = 8;

export type RecentFileMeta = {
  id: string;
  name: string;
  size: number;
  lastOpened: number;
  hasHandle: boolean;
};

type RecentRecord = {
  id: string;
  name: string;
  size: number;
  lastModified: number;
  lastOpened: number;
  handle?: FileSystemFileHandle;
};

function makeId(file: File): string {
  return `${file.name}\0${file.size}\0${file.lastModified}`;
}

function openRawDb(name: string, upgrade: boolean): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = upgrade ? indexedDB.open(name, DB_VERSION) : indexedDB.open(name);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    if (upgrade) {
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
      };
    }
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/**
 * Move recent files from the pre-rename database into the current one, then
 * delete the old database. Only runs when the old database actually exists
 * (indexedDB.open would otherwise create an empty one).
 */
async function migrateLegacyDb(db: IDBDatabase): Promise<void> {
  const list = typeof indexedDB.databases === 'function' ? await indexedDB.databases() : [];
  if (!list.some(d => d.name === LEGACY_DB_NAME)) return;

  const legacy = await openRawDb(LEGACY_DB_NAME, false);
  let records: RecentRecord[] = [];
  if (legacy.objectStoreNames.contains(STORE)) {
    const tx = legacy.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    await txDone(tx);
    records = req.result as RecentRecord[];
  }
  legacy.close();

  if (records.length > 0) {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    // add() keeps any record already saved under the new name.
    for (const r of records) store.add(r).onerror = e => e.preventDefault();
    await txDone(tx);
  }
  indexedDB.deleteDatabase(LEGACY_DB_NAME);
}

let migration: Promise<void> | null = null;

async function openDb(): Promise<IDBDatabase> {
  const db = await openRawDb(DB_NAME, true);
  if (!migration) migration = migrateLegacyDb(db).catch(() => { /* keep going without old recents */ });
  await migration;
  return db;
}

async function ensureHandlePermission(handle: FileSystemFileHandle): Promise<boolean> {
  const opts = { mode: 'read' as const };
  const permissive = handle as FileSystemFileHandle & {
    queryPermission?: (descriptor?: { mode?: 'read' | 'readwrite' }) => Promise<PermissionState>;
    requestPermission?: (descriptor?: { mode?: 'read' | 'readwrite' }) => Promise<PermissionState>;
  };

  try {
    if (typeof permissive.queryPermission === 'function') {
      const state = await permissive.queryPermission(opts);
      if (state === 'granted') return true;
    }
    if (typeof permissive.requestPermission === 'function') {
      return (await permissive.requestPermission(opts)) === 'granted';
    }
  } catch {
    return false;
  }
  // Older Chromium builds granted access by getFile() alone.
  return true;
}

export async function rememberRecentFile(
  file: File,
  handle?: FileSystemFileHandle
): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const id = makeId(file);

      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const existing = getReq.result as RecentRecord | undefined;
        const keptHandle = handle ?? existing?.handle;
        // No handle means the file can never be reopened in one tap: the File
        // System Access API is missing (Safari, so every browser on iOS) or the
        // file arrived by drag and drop. Recording it would put a dead entry on
        // the start screen, so don't.
        if (!keptHandle) return;

        store.put({
          id,
          name: file.name,
          size: file.size,
          lastModified: file.lastModified,
          lastOpened: Date.now(),
          handle: keptHandle
        } satisfies RecentRecord);

        const allReq = store.getAll();
        allReq.onsuccess = () => {
          const all = (allReq.result as RecentRecord[]).sort(
            (a, b) => b.lastOpened - a.lastOpened
          );
          for (const stale of all.slice(MAX_RECENT)) store.delete(stale.id);
        };
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
    db.close();
  } catch {
    // Private mode / blocked storage: recent list is best-effort.
  }
}

export async function listRecentFiles(): Promise<RecentFileMeta[]> {
  try {
    const db = await openDb();
    const all = await new Promise<RecentRecord[]>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result as RecentRecord[]);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB getAll failed'));
    });
    db.close();
    return all
      // Entries without a handle cannot be reopened; older builds stored them,
      // so filter here as well and the list simply stays empty on iOS.
      .filter(r => !!r.handle)
      .sort((a, b) => b.lastOpened - a.lastOpened)
      .slice(0, MAX_RECENT)
      .map(r => ({
        id: r.id,
        name: r.name,
        size: r.size,
        lastOpened: r.lastOpened,
        hasHandle: true
      }));
  } catch {
    return [];
  }
}

/** Re-open a recent file via its stored handle. Returns null if missing or permission denied. */
export async function openRecentFile(
  id: string
): Promise<{ file: File; handle: FileSystemFileHandle } | null> {
  try {
    const db = await openDb();
    const record = await new Promise<RecentRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => resolve(req.result as RecentRecord | undefined);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB get failed'));
    });
    db.close();

    if (!record?.handle) return null;
    if (!(await ensureHandlePermission(record.handle))) return null;
    const file = await record.handle.getFile();
    return { file, handle: record.handle };
  } catch {
    return null;
  }
}
